import { EMPTY_ARRAY } from '@e-socna/empty';

import { DocumentEditorActionType } from '../../enums/action-type.enum';
import { DocumentEditorInstantaneousContentActionType } from '../../enums/instantaneous-content-action-type.enum';
import { FoldingMode } from '../../enums/folding-mode.enum';
import { DocumentEditorTwoStepContentActionType } from '../../enums/two-step-content-action-type.enum';
import type { DocumentEditorState } from '../../types/editor-state.type';
import type { Editable } from '../../types/editable.type';
import type { DocumentEditorAction } from '../../types/actions.type';
import type { DocumentEditorContentAction } from '../../types/content-actions.type';

import type { ReducerContext } from '../types/reducer-context.type';

export const uiReducer = <E extends Editable>(
  state: DocumentEditorState<E>,
  action: DocumentEditorAction<E> | DocumentEditorContentAction,
  _context: Readonly<ReducerContext<E>>,
): DocumentEditorState<E> | null => {
  switch (action.type) {
    case DocumentEditorActionType.OpenDrawer: {
      return {
        ...state,
        isDrawerOpen: true,
      };
    }

    case DocumentEditorActionType.CloseDrawer: {
      return {
        ...state,
        isDrawerOpen: false,
      };
    }

    case DocumentEditorActionType.FoldAllSections: {
      return {
        ...state,
        foldedSectionIds: action.payload.sectionIds,
      };
    }

    case DocumentEditorActionType.UnfoldAllSections: {
      return {
        ...state,
        foldedSectionIds: EMPTY_ARRAY,
      };
    }

    case DocumentEditorInstantaneousContentActionType.SetFoldingMode: {
      if (action.payload.foldingMode === FoldingMode.Folded) {
        return {
          ...state,
          foldedSectionIds: [
            ...new Set([...state.foldedSectionIds, action.payload.id]),
          ],
        };
      }

      return {
        ...state,
        foldedSectionIds: state.foldedSectionIds.filter(
          (id) => id !== action.payload.id,
        ),
      };
    }

    case DocumentEditorActionType.SetEditorMode:
    case DocumentEditorActionType.RequestTwoStepContentAction:
    case DocumentEditorActionType.ClearTwoStepContentAction:
    case DocumentEditorActionType.Undo:
    case DocumentEditorActionType.Redo:
    case DocumentEditorActionType.ClearHistory:
    case DocumentEditorActionType.ClearNavigationTarget:
    case DocumentEditorActionType.BumpLatestSavedVersion:
    case DocumentEditorActionType.UpdateInfo:
    case DocumentEditorActionType.UpdateAdmin:
    case DocumentEditorInstantaneousContentActionType.Duplicate:
    case DocumentEditorInstantaneousContentActionType.Move:
    case DocumentEditorInstantaneousContentActionType.Delete:
    case DocumentEditorInstantaneousContentActionType.Reinitialize:
    case DocumentEditorInstantaneousContentActionType.SetCustomPageBreakBefore:
    case DocumentEditorInstantaneousContentActionType.SetPageBreakBefore:
    case DocumentEditorInstantaneousContentActionType.SetCustomPageBreakAfter:
    case DocumentEditorInstantaneousContentActionType.SetPageBreakAfter:
    case DocumentEditorInstantaneousContentActionType.SetOrientation:
    case DocumentEditorInstantaneousContentActionType.SetPaperFormat:
    case DocumentEditorInstantaneousContentActionType.SetDisplayCondition:
    case DocumentEditorTwoStepContentActionType.InsertBefore:
    case DocumentEditorTwoStepContentActionType.InsertAfter:
    case DocumentEditorTwoStepContentActionType.InsertInside:
    case DocumentEditorTwoStepContentActionType.InsertStart:
    case DocumentEditorTwoStepContentActionType.InsertEnd:
    case DocumentEditorTwoStepContentActionType.Update:
    default: {
      return null; // Not handled by this reducer
    }
  }
};
